import React, { useState } from "react";
import { Star, CheckCircle, RotateCcw, Send } from "lucide-react";
import { getSocket } from "../utils/useChatSocket";

const socket = getSocket();

const FeedbackForm = ({
  selectedConversation,
  setSelectedConversation,
  setConversations,
  setupDetails,
}) => {
  const [rating, setRating] = useState(5);
  const [resolved, setResolved] = useState("Yes");
  const [comments, setComments] = useState("");
  const [hover, setHover] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const primaryColor =
    setupDetails?.widgetCustomizations?.primaryColor ||
    setupDetails?.widgetCustomizations?.color1 ||
    "#2563eb";
  const secondaryColor =
    setupDetails?.widgetCustomizations?.secondaryColor ||
    setupDetails?.widgetCustomizations?.color2 ||
    "#3b82f6";

  const primaryShade = (opacity) => {
    const hex = primaryColor.replace("#", "");
    if (hex.length === 6) {
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    return `rgba(37, 99, 235, ${opacity})`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    const feedbackData = { rating, resolved, comments };
    const ticketId = selectedConversation?.ticketId?._id;
    const roomId = selectedConversation?.roomId;

    socket.emit(
      "closeTicket",
      { ticketId, roomId, feedback: feedbackData },
      (response) => {
        setSubmitting(false);
        if (response && response.success) {
          setConversations((prevConversations) =>
            prevConversations.map((conversation) =>
              conversation.roomId === roomId
                ? response.conversation
                : conversation
            )
          );

          if (selectedConversation && roomId === selectedConversation.roomId) {
            setSelectedConversation(response.conversation);
          }
        } else {
          console.error("Error closing ticket:", response?.message);
        }
      }
    );
  };

  const handleReopen = () => {
    const newStatus = "open";
    const ticketId = selectedConversation?.ticketId?._id;
    const roomId = selectedConversation?.roomId;

    socket.emit(
      "updateTicketStatus",
      {
        ticketId,
        status: newStatus,
        roomId,
      },
      (response) => {
        if (response && response.success) {
          setConversations((prevConversations) =>
            prevConversations.map((conversation) => {
              if (conversation.roomId === roomId) {
                return {
                  ...conversation,
                  ticketId: {
                    ...conversation.ticketId,
                    status: newStatus,
                  },
                };
              }
              return conversation;
            })
          );

          if (selectedConversation && roomId === selectedConversation.roomId) {
            setSelectedConversation((prev) => ({
              ...prev,
              ticketId: {
                ...prev.ticketId,
                status: newStatus,
              },
            }));
          }
        } else {
          console.error("Error updating ticket status:", response?.message);
        }
      }
    );
  };

  return (
    <div className="w-full bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 text-left space-y-3.5 animate-in fade-in duration-200">
      {/* Resolution Prompt Banner */}
      <div className="flex items-center gap-2 pb-2.5 border-b border-slate-100">
        <div className="w-7 h-7 rounded-lg bg-emerald-50 border border-emerald-200/70 flex items-center justify-center shrink-0">
          <CheckCircle className="w-4 h-4 text-emerald-600" />
        </div>
        <div>
          <h4 className="text-xs font-bold text-slate-900 leading-snug">
            Your ticket was marked as resolved
          </h4>
          <p className="text-[11px] text-slate-500">
            Please share your feedback to complete this request.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Was issue resolved? */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">
            Was your issue resolved?
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setResolved("Yes")}
              className={`flex-1 py-2 px-3 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${
                resolved === "Yes"
                  ? "bg-emerald-50 text-emerald-700 border-emerald-300 shadow-xs"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              ✓ Yes, resolved
            </button>
            <button
              type="button"
              onClick={() => setResolved("No")}
              className={`flex-1 py-2 px-3 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${
                resolved === "No"
                  ? "bg-rose-50 text-rose-700 border-rose-300 shadow-xs"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              ✕ No, still an issue
            </button>
          </div>
        </div>

        {resolved === "No" ? (
          <div className="p-3 bg-amber-50/70 border border-amber-200/70 rounded-xl space-y-2">
            <p className="text-xs text-amber-800 leading-relaxed">
              If you still need help with this ticket, our support team can reopen it for you right away.
            </p>
            <button
              type="button"
              onClick={handleReopen}
              className="w-full py-2 px-3 rounded-lg text-xs font-bold text-white bg-slate-800 hover:bg-slate-900 transition-colors flex items-center justify-center gap-1.5 cursor-pointer border-none"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Reopen This Conversation</span>
            </button>
          </div>
        ) : (
          <>
            {/* Star Rating */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                How would you rate our support?
              </label>
              <div className="flex items-center gap-1.5">
                {[1, 2, 3, 4, 5].map((index) => {
                  const active = (hover || rating) >= index;
                  return (
                    <button
                      key={index}
                      type="button"
                      onMouseEnter={() => setHover(index)}
                      onMouseLeave={() => setHover(null)}
                      onClick={() => setRating(index)}
                      className="p-1 hover:scale-110 active:scale-95 transition-transform border-none bg-transparent cursor-pointer"
                    >
                      <Star
                        className={`w-6 h-6 transition-colors ${
                          active
                            ? "text-amber-400 fill-amber-400"
                            : "text-slate-200 fill-slate-100"
                        }`}
                      />
                    </button>
                  );
                })}
                <span className="text-xs font-bold text-slate-700 ml-2">
                  {rating === 5 && "Excellent!"}
                  {rating === 4 && "Very Good"}
                  {rating === 3 && "Good"}
                  {rating === 2 && "Fair"}
                  {rating === 1 && "Poor"}
                </span>
              </div>
            </div>

            {/* Comment */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Any comments or suggestions? (Optional)
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={2}
                placeholder="Share your thoughts about your experience..."
                className="w-full p-2.5 text-xs text-slate-800 placeholder-slate-400 bg-slate-50/70 hover:bg-slate-50 focus:bg-white rounded-xl border border-slate-200 focus:border-transparent focus:outline-none focus:ring-2 transition-all resize-none"
                style={{
                  "--tw-ring-color": primaryColor,
                }}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-2.5 px-4 rounded-xl text-xs font-bold text-white flex items-center justify-center gap-2 cursor-pointer transition-all hover:opacity-95 active:scale-[0.99] border-none outline-none shadow-sm disabled:opacity-60"
              style={{
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                boxShadow: `0 4px 12px ${primaryShade(0.28)}`,
              }}
            >
              {submitting ? (
                <span>Submitting...</span>
              ) : (
                <>
                  <span>Submit & Close Ticket</span>
                  <Send className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          </>
        )}
      </form>
    </div>
  );
};

export default FeedbackForm;
